/**
 * Admin → "AI-analyysiohje" tab.
 *
 * Lets an admin edit the "metsäsijoittamisen ohjekirja" — the instructions the
 * backend uses as the LLM system prompt when writing an estate's narrative
 * profitability analysis. Previously these instructions were hard-coded in
 * `backend/internal/agents`.
 *
 * The shipped default stays available via "Palauta oletusohje", so a bad edit
 * is always recoverable. The re-analysis box reruns one estate from its stored
 * structured data (no scraping) so an edit can be verified immediately.
 */
import {
  getAnalysisPrompt,
  setAnalysisPrompt,
  resetAnalysisPrompt,
  reanalyzeEstate,
  type AnalysisPrompt,
} from '../api/admin.ts';
import { currentUser } from '../state/session.ts';
import { t, getLang } from '../i18n/index.ts';
import { APIRequestError } from '../api/client.ts';

export async function renderAdminAnalysisPrompt(root: HTMLElement): Promise<void> {
  const u = currentUser();
  if (!u || u.role !== 'admin') {
    root.innerHTML = `<div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(t('admin_only'))}</div>`;
    return;
  }

  root.innerHTML = `<div class="placeholder-card">${escapeHtml(t('view_admin_loading'))}</div>`;

  let cfg: AnalysisPrompt;
  try {
    cfg = await getAnalysisPrompt();
  } catch (e) {
    root.innerHTML = `<div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(formatErr(e as Error))}</div>`;
    return;
  }

  root.innerHTML = shell(cfg);
  bind(root);
}

function shell(cfg: AnalysisPrompt): string {
  return `
    <div class="admin-form">
      <div class="admin-sub">${escapeHtml(t('admin_prompt_sub'))}</div>

      <div class="admin-form-row" style="justify-content:space-between;align-items:center">
        <div id="promptMeta" class="admin-sub">${metaText(cfg)}</div>
        <div id="promptStatus" class="admin-sub" aria-live="polite"></div>
      </div>

      <label class="admin-label" for="promptText">${escapeHtml(t('admin_prompt_label'))}</label>
      <textarea id="promptText" class="admin-input" rows="24"
        spellcheck="false"
        style="font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;line-height:1.5;white-space:pre;overflow-wrap:normal;overflow-x:auto"
      >${escapeHtml(cfg.prompt)}</textarea>

      <div class="admin-form-row">
        <button id="promptSave" class="btn btn-primary">${escapeHtml(t('admin_prompt_save'))}</button>
        <button id="promptReset" class="btn">${escapeHtml(t('admin_prompt_reset'))}</button>
      </div>

      <hr style="margin:1.5em 0;border:none;border-top:1px solid rgba(0,0,0,.1)" />

      <div class="admin-label">${escapeHtml(t('admin_prompt_test_title'))}</div>
      <div class="admin-sub">${escapeHtml(t('admin_prompt_test_sub'))}</div>
      <div class="admin-form-row">
        <input id="promptEstateId" class="admin-input" type="number" min="1"
          placeholder="${escapeHtml(t('admin_prompt_estate_id'))}" style="max-width:12em" />
        <button id="promptRun" class="btn">${escapeHtml(t('admin_prompt_run'))}</button>
      </div>
      <div id="promptResult"></div>
    </div>
  `;
}

function metaText(cfg: AnalysisPrompt): string {
  if (cfg.is_default) return escapeHtml(t('admin_prompt_is_default'));
  const when = cfg.updated_at ? formatDate(cfg.updated_at) : '—';
  return escapeHtml(`${t('admin_prompt_is_custom')} (${when})`);
}

function bind(root: HTMLElement): void {
  const ta = root.querySelector<HTMLTextAreaElement>('#promptText')!;
  const save = root.querySelector<HTMLButtonElement>('#promptSave')!;
  const reset = root.querySelector<HTMLButtonElement>('#promptReset')!;
  const run = root.querySelector<HTMLButtonElement>('#promptRun')!;
  const idInput = root.querySelector<HTMLInputElement>('#promptEstateId')!;
  const status = root.querySelector<HTMLDivElement>('#promptStatus')!;
  const meta = root.querySelector<HTMLDivElement>('#promptMeta')!;
  const result = root.querySelector<HTMLDivElement>('#promptResult')!;

  const setStatus = (msg: string, error = false): void => {
    status.textContent = msg;
    status.style.color = error ? 'rgb(140,55,55)' : '';
  };

  save.addEventListener('click', async () => {
    if (!ta.value.trim()) {
      setStatus(t('admin_prompt_empty'), true);
      return;
    }
    save.disabled = true;
    setStatus(t('admin_prompt_saving'));
    try {
      const cfg = await setAnalysisPrompt(ta.value);
      ta.value = cfg.prompt;
      meta.innerHTML = metaText(cfg);
      setStatus(t('admin_prompt_saved'));
    } catch (e) {
      setStatus(formatErr(e as Error), true);
    } finally {
      save.disabled = false;
    }
  });

  reset.addEventListener('click', async () => {
    if (!confirm(t('admin_prompt_reset_confirm'))) return;
    reset.disabled = true;
    setStatus(t('admin_prompt_saving'));
    try {
      const cfg = await resetAnalysisPrompt();
      ta.value = cfg.prompt;
      meta.innerHTML = metaText(cfg);
      setStatus(t('admin_prompt_reset_done'));
    } catch (e) {
      setStatus(formatErr(e as Error), true);
    } finally {
      reset.disabled = false;
    }
  });

  run.addEventListener('click', async () => {
    const id = Number(idInput.value);
    if (!Number.isInteger(id) || id <= 0) {
      result.innerHTML = `<div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(t('admin_prompt_bad_id'))}</div>`;
      return;
    }
    run.disabled = true;
    // The analysis stage is a live LLM call and can take tens of seconds on a
    // cold cloud model, so say so rather than looking hung.
    result.innerHTML = `<div class="placeholder-card">${escapeHtml(t('admin_prompt_running'))}</div>`;
    try {
      const res = await reanalyzeEstate(id);
      result.innerHTML = `
        <div class="placeholder-card">
          <div class="admin-label">${escapeHtml(res.name || `#${res.id}`)}</div>
          <div style="white-space:pre-wrap">${escapeHtml(res.analysis)}</div>
        </div>`;
    } catch (e) {
      result.innerHTML = `<div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(formatErr(e as Error))}</div>`;
    } finally {
      run.disabled = false;
    }
  });
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleString(getLang() === 'en' ? 'en-GB' : 'fi-FI');
  } catch {
    return s;
  }
}
function formatErr(e: Error): string {
  if (e instanceof APIRequestError) return e.body.error || e.message;
  return e.message;
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
